import * as XLSX from 'xlsx';
import firebaseService from '../firebase.service';
import { buildMediaIndex } from './fileUtils';

// Convert Excel serial number to readable date
const convertExcelDate = (excelSerial) => {
  if (!excelSerial || isNaN(excelSerial)) return excelSerial;
  
  // Excel serial number to JavaScript date conversion
  // Excel counts days from 1900-01-01, but has a leap year bug
  const excelEpoch = new Date(1900, 0, 1);
  const jsDate = new Date(excelEpoch.getTime() + (excelSerial - 2) * 24 * 60 * 60 * 1000);
  
  // Format as readable date
  return jsDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Check if a value is an Excel serial date
const isExcelSerialDate = (value) => {
  if (!value || isNaN(value)) return false;
  const num = parseFloat(value);
  // Excel serial dates are typically between 1 (1900-01-01) and 50000 (2036-12-31)
  return num >= 1 && num <= 50000;
};

// Format date to the new format (1-Jul-2024)
const formatDateToNewFormat = (dateValue) => {
  if (!dateValue) return dateValue;
  
  // If it's already in the new format (contains month abbreviation), return as is
  if (typeof dateValue === 'string' && /^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(dateValue.trim())) {
    return dateValue.trim();
  }
  
  // If it's an Excel serial number, convert it
  if (isExcelSerialDate(dateValue)) {
    const convertedDate = convertExcelDate(dateValue);
    return convertToNewFormat(convertedDate);
  }
  
  // If it's already a readable date, convert to new format
  if (typeof dateValue === 'string' && dateValue.includes('/')) {
    return convertToNewFormat(dateValue);
  }
  
  return dateValue;
};

// Convert date string to new format (1-Jul-2024)
const convertToNewFormat = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (error) {
    return dateString;
  }
};

// Process Excel file and create individual book records
export const processExcelToIndividualRecords = async (excelFile, project, onProgress) => {
  try {
    console.log('🔄 Starting Excel processing for individual records...');
    
    // Read Excel file
    const arrayBuffer = await excelFile.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    console.log(`📊 Excel file info:`);
    console.log(`📊 Sheet names: ${workbook.SheetNames.join(', ')}`);
    console.log(`📊 Using sheet: ${firstSheetName}`);
    console.log(`📊 Worksheet range: ${worksheet['!ref']}`);
    
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    console.log(`📊 Raw JSON data length: ${jsonData.length}`);
    console.log(`📊 First few rows:`, jsonData.slice(0, 3));
    
    if (jsonData.length < 2) {
      throw new Error('Excel file has no data rows');
    }
    
    const headers = jsonData[0];
    const books = [];
    const metadata = {
      project: project.toUpperCase(),
      totalBooks: 0,
      lastUpdated: new Date().toISOString(),
      books: [] // Lightweight list for BookList
    };
    
    // Find code column index with more comprehensive search
    let codeColumnIndex = headers.findIndex(header => 
      header && header.toLowerCase().includes('code')
    );
    
    if (codeColumnIndex === -1) {
      codeColumnIndex = headers.findIndex(header => 
        header && (
          header.toLowerCase().includes('book_code') ||
          header.toLowerCase().includes('bookcode') ||
          header.toLowerCase().includes('id') ||
          header.toLowerCase().includes('identifier') ||
          header.toLowerCase().includes('book_id') ||
          header.toLowerCase().includes('bookid')
        )
      );
    }
    
    // If still not found, try to find any column that might contain codes
    if (codeColumnIndex === -1) {
      console.log(`🔍 No standard code column found. Checking all headers for potential code columns:`);
      headers.forEach((header, index) => {
        if (header) {
          console.log(`  Column ${index}: "${header}"`);
        }
      });
      
      // Try to find the first column that might contain codes (look for patterns)
      for (let i = 0; i < Math.min(5, headers.length); i++) {
        const header = headers[i];
        if (header && header.trim()) {
          console.log(`🔍 Trying column ${i} ("${header}") as potential code column`);
          codeColumnIndex = i;
          break;
        }
      }
    }
    
    console.log(`🔍 Code column index: ${codeColumnIndex}, Header: ${headers[codeColumnIndex]}`);
    console.log(`📊 Total rows in Excel: ${jsonData.length} (including header)`);
    console.log(`📊 Data rows to process: ${jsonData.length - 1}`);
    console.log(`📋 Headers found:`, headers.slice(0, 10)); // Show first 10 headers
    console.log(`📋 ALL HEADERS:`, headers); // Show all headers for debugging
    
    // First pass: Collect all valid books
    const validBooks = [];
    let emptyRows = 0;
    let filteredRows = 0;
    
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      
      // Debug first few rows and every 100th row
      if (i <= 5 || i % 100 === 0) {
        console.log(`🔍 Row ${i} data:`, row);
      }
      
      // Debug progress every 200 rows
      if (i % 200 === 0) {
        console.log(`🔄 Processing row ${i}/${jsonData.length - 1} (${((i / (jsonData.length - 1)) * 100).toFixed(1)}%)`);
      }
      
      if (row && row.length > 0) {
        const book = {};
        
        // Map each column to book property with flexible header matching
        headers.forEach((header, index) => {
          if (header && row[index] !== undefined) {
            const cleanHeader = header.trim().toLowerCase();
            let value = String(row[index]).trim();
            
            // Check if this is a date field and convert to new format
            if (cleanHeader.includes('date') || cleanHeader.includes('publication') || cleanHeader.includes('print')) {
              const rawValue = row[index];
              value = formatDateToNewFormat(rawValue);
              if (rawValue !== value) {
                console.log(`📅 Formatted date for ${header}: ${rawValue} -> ${value}`);
              }
            }
            
            // Also keep the original header mapping
            book[header.trim()] = value;

            // Skip compound/contact fields — they are preserved via the
            // original header mapping above (e.g. copyright_contact_person_name_en).
            // Without this guard, "copyright_contact_person_name_en" would match
            // the 'name' + 'en' rule and overwrite the book title.
            const isContactField = cleanHeader.includes('copyright') ||
              cleanHeader.includes('contact') ||
              cleanHeader.includes('phone') ||
              cleanHeader.includes('fax') ||
              cleanHeader.includes('email') ||
              cleanHeader.includes('position');

            if (isContactField) {
              return; // skip to next header
            }

            // Helper: check if header signals a specific language
            const hasLangEn = cleanHeader.includes('_en') || cleanHeader.includes('en)') || cleanHeader.includes('english');
            const hasLangTc = cleanHeader.includes('_tc') || cleanHeader.includes('tc)') || cleanHeader.includes('traditional');
            const hasLangSc = cleanHeader.includes('_sc') || cleanHeader.includes('sc)') || cleanHeader.includes('simplified');

            // Map standard field names — use startsWith or word-boundary-aware
            // checks so "name_en" matches but "person_name_en" does not.
            const startsOrIs = (keyword) => {
              return cleanHeader === keyword ||
                cleanHeader.startsWith(keyword + '_') ||
                cleanHeader.startsWith(keyword + ' ') ||
                cleanHeader.startsWith('book_' + keyword) ||
                cleanHeader.startsWith('book ' + keyword);
            };

            if ((startsOrIs('name') || startsOrIs('title')) && hasLangEn) {
              if (!book.name_en) book.name_en = value;
            } else if ((startsOrIs('name') || startsOrIs('title')) && hasLangTc) {
              if (!book.name_tc) book.name_tc = value;
            } else if ((startsOrIs('name') || startsOrIs('title')) && hasLangSc) {
              if (!book.name_sc) book.name_sc = value;
            } else if (startsOrIs('publisher') && hasLangEn) {
              book.publisher_en = value;
            } else if (startsOrIs('publisher') && hasLangTc) {
              book.publisher_tc = value;
            } else if (startsOrIs('publisher') && hasLangSc) {
              book.publisher_sc = value;
            } else if (startsOrIs('author') && hasLangEn) {
              book.author_en = value;
            } else if (startsOrIs('author') && hasLangTc) {
              book.author_tc = value;
            } else if (startsOrIs('author') && hasLangSc) {
              book.author_sc = value;
            } else if (startsOrIs('introduction') && hasLangEn) {
              book.introduction_en = value;
            } else if (startsOrIs('introduction') && hasLangTc) {
              book.introduction_tc = value;
            } else if (startsOrIs('introduction') && hasLangSc) {
              book.introduction_sc = value;
            } else if (startsOrIs('type') && hasLangEn) {
              book.type_en = value;
            } else if (startsOrIs('type') && hasLangTc) {
              book.type_tc = value;
            } else if (startsOrIs('type') && hasLangSc) {
              book.type_sc = value;
            } else if (startsOrIs('category') && hasLangEn) {
              book.category_en = value;
            } else if (startsOrIs('category') && hasLangTc) {
              book.category_tc = value;
            } else if (startsOrIs('category') && hasLangSc) {
              book.category_sc = value;
            } else if (cleanHeader.includes('company') || cleanHeader.includes('exhibitor')) {
              book.company = value;
            } else if (cleanHeader.includes('redirect') || cleanHeader === 'link' || cleanHeader === 'url' || cleanHeader.includes('website')) {
              book.redirect_link = value;
            }
          }
        });
        
        // Set code from identified column
        if (codeColumnIndex !== -1 && row[codeColumnIndex]) {
          book.code = String(row[codeColumnIndex]).trim();
        } else if (codeColumnIndex === -1) {
          // If no code column was found, try to use the first column as code
          if (row[0]) {
            book.code = String(row[0]).trim();
          }
        }
        
        // Debug first few books
        if (i <= 3) {
          console.log(`📖 Book ${i} mapped data:`, {
            code: book.code,
            name_en: book.name_en,
            name_tc: book.name_tc,
            name_sc: book.name_sc,
            company: book.company,
            publisher_en: book.publisher_en,
            redirect_link: book.redirect_link
          });
          console.log(`📖 Book ${i} ALL MAPPED FIELDS:`, book);
        }
        
        // Process books that have any meaningful data (more flexible validation)
        const hasName = book.name_en || book.name_tc || book.name_sc;
        const hasCode = book.code && book.code.toString().trim();
        const hasOtherData = book.company || book.publisher_en || book.publisher_tc || book.publisher_sc || 
                           book.author_en || book.author_tc || book.author_sc;
        
        // Include books that have at least a name, code, or other meaningful data
        if (hasName || hasCode || hasOtherData) {
          validBooks.push(book);
        } else {
          filteredRows++;
          if (filteredRows <= 5) {
            console.log(`❌ Row ${i} filtered out - no meaningful data:`, {
              hasName, hasCode, hasOtherData,
              name_en: book.name_en,
              name_tc: book.name_tc, 
              name_sc: book.name_sc,
              code: book.code,
              company: book.company,
              publisher_en: book.publisher_en
            });
          }
        }
      } else {
        emptyRows++;
        if (emptyRows <= 3) {
          console.log(`⚠️ Row ${i} is empty or has no data`);
        }
      }
    }
    
    console.log(`📊 PROCESSING SUMMARY:`);
    console.log(`📊 Total Excel rows: ${jsonData.length} (including header)`);
    console.log(`📊 Data rows to process: ${jsonData.length - 1}`);
    console.log(`📊 Rows actually processed: ${jsonData.length - 1 - emptyRows}`);
    console.log(`📊 Empty rows: ${emptyRows}`);
    console.log(`📊 Filtered out rows: ${filteredRows}`);
    console.log(`📊 Valid books found: ${validBooks.length}`);
    console.log(`📊 Success rate: ${((validBooks.length / (jsonData.length - 1)) * 100).toFixed(1)}%`);
    
    // Additional debugging for the specific issue
    if (validBooks.length === 81) {
      console.log(`🔍 DEBUG: Found exactly 81 books - this matches the user's report`);
      console.log(`🔍 This suggests the processing might be stopping early or there's a specific pattern in the data`);
    }
    
    if (validBooks.length < (jsonData.length - 1) * 0.5) {
      console.log(`⚠️ WARNING: Less than 50% of rows are being processed!`);
      console.log(`⚠️ This suggests there might be an issue with the Excel structure or column mapping.`);
    }
    
    // Build media index once (2 API calls total) instead of per-book checks
    console.log(`🔍 Building media index for ${project}...`);
    const { lookup: mediaLookup } = await buildMediaIndex(project);

    // Second pass: attach media info and upload in batches
    const batchSize = 50;
    let processedCount = 0;
    
    for (let i = 0; i < validBooks.length; i += batchSize) {
      const batch = validBooks.slice(i, i + batchSize);
      console.log(`🔄 Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(validBooks.length/batchSize)} (${batch.length} books)`);
      
      const batchWithMedia = batch.map((book) => {
        const mediaType = mediaLookup(book.code);
        const hasCover = mediaType === 'image';
        const hasModel = mediaType === 'glb';
        
        const individualBook = {
          ...book,
          mediaType,
          hasCover,
          hasModel,
          coverUrl: hasCover ? `https://firebasestorage.googleapis.com/v0/b/bookfair-infobox-qr.firebasestorage.app/o/${project.toUpperCase()}%2Fcovers%2F${book.code}.webp?alt=media` : null,
          modelUrl: hasModel ? `https://firebasestorage.googleapis.com/v0/b/bookfair-infobox-qr.firebasestorage.app/o/${project.toUpperCase()}%2Fmodels%2F${book.code}.glb?alt=media` : null,
          lastUpdated: new Date().toISOString()
        };
        
        const lightweightBook = {
          code: book.code,
          name_en: book.name_en,
          name_tc: book.name_tc,
          name_sc: book.name_sc,
          company: book.company,
          publisher_en: book.publisher_en,
          publisher_tc: book.publisher_tc,
          publisher_sc: book.publisher_sc,
          author_en: book.author_en,
          author_tc: book.author_tc,
          author_sc: book.author_sc,
          type_en: book.type_en,
          type_tc: book.type_tc,
          type_sc: book.type_sc,
          category_en: book.category_en,
          category_tc: book.category_tc,
          category_sc: book.category_sc,
          redirect_link: book.redirect_link,
          mediaType,
          hasCover,
          hasModel
        };
        
        return { individualBook, lightweightBook };
      });
      
      // Upload batch in parallel
      await processBatch(batchWithMedia.map(item => item.individualBook), project);
      
      // Add to collections
      batchWithMedia.forEach(({ individualBook, lightweightBook }) => {
        books.push(individualBook);
        metadata.books.push(lightweightBook);
      });
      
      processedCount += batch.length;
      onProgress && onProgress(processedCount, validBooks.length);
    }
    
    // Update metadata
    metadata.totalBooks = books.length;
    await uploadMetadata(metadata, project);
    
    // Clean up orphaned book JSONs no longer in the new Excel
    const currentBookCodes = books.map(b => b.code);
    let removedCodes = [];
    try {
      removedCodes = await cleanupOldRecords(currentBookCodes, project);
      if (removedCodes.length > 0) {
        console.log(`🧹 Removed ${removedCodes.length} outdated book(s): ${removedCodes.join(', ')}`);
      }
    } catch (err) {
      console.error('⚠️ Orphan cleanup failed (non-fatal):', err);
    }
    
    console.log(`✅ Processed ${books.length} individual book records`);
    return { processedCount: books.length, removedCodes };
    
  } catch (error) {
    console.error('❌ Error processing Excel to individual records:', error);
    throw error;
  }
};

// Process a batch of books
const processBatch = async (books, project) => {
  const uploadPromises = books.map(async (book) => {
    try {
      // Create JSON file for individual book
      const jsonContent = JSON.stringify(book, null, 2);
      const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
      const jsonFile = new File([jsonBlob], `${book.code}.json`, { type: 'application/json' });
      
      // Upload individual book JSON
      const uploadPath = `${project.toUpperCase()}/books/${book.code}.json`;
      await firebaseService.uploadFile(jsonFile, uploadPath);
      
      console.log(`📦 Uploaded individual record for ${book.code}`);
    } catch (error) {
      console.error(`❌ Failed to upload ${book.code}:`, error);
    }
  });
  
  await Promise.all(uploadPromises);
};

// Upload metadata file
const uploadMetadata = async (metadata, project) => {
  try {
    const jsonContent = JSON.stringify(metadata, null, 2);
    const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
    const jsonFile = new File([jsonBlob], 'metadata.json', { type: 'application/json' });
    
    const uploadPath = `${project.toUpperCase()}/books/metadata.json`;
    await firebaseService.uploadFile(jsonFile, uploadPath);
    
    console.log(`📊 Uploaded metadata for ${project}`);
  } catch (error) {
    console.error('❌ Failed to upload metadata:', error);
    throw error;
  }
};

// Clean up old book records that no longer exist in new Excel
export const cleanupOldRecords = async (currentBookCodes, project) => {
  try {
    console.log('🧹 Cleaning up old book records...');
    
    // Get list of existing book files
    const existingBooks = await getExistingBookFiles(project);
    const currentCodes = new Set(currentBookCodes);
    
    // Find books to delete
    const booksToDelete = existingBooks.filter(bookCode => !currentCodes.has(bookCode));
    
    console.log(`🗑️ Found ${booksToDelete.length} books to delete:`, booksToDelete);
    
    // Delete old book files
    for (const bookCode of booksToDelete) {
      try {
        await firebaseService.deleteFile(`${project.toUpperCase()}/books/${bookCode}.json`);
        console.log(`✅ Deleted old record: ${bookCode}`);
      } catch (error) {
        console.error(`❌ Failed to delete ${bookCode}:`, error);
      }
    }
    
    return booksToDelete;
  } catch (error) {
    console.error('❌ Error cleaning up old records:', error);
    return [];
  }
};

// Get list of existing book files from Firebase Storage
const getExistingBookFiles = async (project) => {
  try {
    const booksPath = `${project.toUpperCase()}/books`;
    const fileNames = await firebaseService.listFileNames(booksPath);
    return fileNames
      .filter(name => name.endsWith('.json') && name !== 'metadata.json')
      .map(name => name.replace('.json', ''));
  } catch (error) {
    console.error('❌ Error getting existing book files:', error);
    return [];
  }
};
